import {
  apolloClient,
  fetchBanners,
  fetchMarketById,
  fetchPage,
  fetchPageLinks,
  GetFaqCategoriesQuery,
  GetFaqCategoriesQueryVariables,
  GetFaqsQuery,
  GetFaqsQueryVariables,
  GET_FAQS_QUERY,
  GET_FAQ_CATEGORIES_QUERY,
  localeToMarketCode,
  PageProps,
} from '@instabox/consumer-web-shared'
import { Stack } from '@instabox/consumer-web-ui'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'

import ContactUs from '../../../components/ContactUs'
import FAQCategory from '../../../components/FAQCategory'
import PageContent from '../../../components/PageContent'
import FAQSearchContainer from '../../../containers/FAQSearchContainer'
import useFaqsAndCategories from '../../../hooks/useFaqsAndCategories'
import { CategoryWithFaqs } from '../../../types'
import getCategoriesWithFaqs from '../../../utils/mapCategoriesWithFaqs'

export interface FAQPageProps extends PageProps {
  category: CategoryWithFaqs
}

const SLUG = 'help'

const FAQPage: NextPage<FAQPageProps> = ({ category, market, content }) => {
  const { data: { faqs = [], categories = [] } = {} } = useFaqsAndCategories()

  return (
    <Stack gap="xl">
      <PageContent content={content} slug={SLUG} />
      <FAQSearchContainer categories={categories} faqs={faqs} />
      <div className="row">
        <div className="col-12 col-lg-8 align-self-start">
          <FAQCategory {...category} />
        </div>
        <div className="col-12 col-lg-4">
          {market && <ContactUs headingSize="sm" {...market} />}
        </div>
      </div>
    </Stack>
  )
}

export default FAQPage

export const getStaticProps: GetStaticProps<FAQPageProps> = async ({
  params: { slug },
  locale,
}) => {
  const marketCode = localeToMarketCode(locale)
  const marketFilter = {
    markets: {
      markets_code: { code: { _eq: marketCode } },
    },
  }
  const translationFilter = {
    languages_code: {
      _eq: locale,
    },
  }

  const {
    data: { faqCategories },
  } = await apolloClient.query<GetFaqCategoriesQuery, GetFaqCategoriesQueryVariables>({
    query: GET_FAQ_CATEGORIES_QUERY,
    variables: {
      filter: {
        slug: {
          _eq: slug as string,
        },
      },
      translations_filter: translationFilter,
    },
  })

  const faqCategory = faqCategories[0]

  if (!faqCategory) {
    return { notFound: true, revalidate: 60 }
  }

  const categoryIdFilter = {
    categories: {
      faq_categories_id: {
        parent_id: {
          id: {
            _eq: Number(faqCategory.id),
          },
        },
      },
    },
  }

  const subCategoryIdFilter = {
    categories: {
      faq_categories_id: {
        id: {
          _eq: Number(faqCategory.id),
        },
      },
    },
  }

  const categoryFilter =
    faqCategory.sub_categories?.length > 0 ? categoryIdFilter : subCategoryIdFilter

  const {
    data: { faqs },
  } = await apolloClient.query<GetFaqsQuery, GetFaqsQueryVariables>({
    query: GET_FAQS_QUERY,
    variables: {
      filter: {
        ...marketFilter,
        ...categoryFilter,
      },
      translations_filter: translationFilter,
    },
  })

  const categoriesWithFaqs = getCategoriesWithFaqs({ faqCategories, faqs })

  const {
    data: { marketById },
  } = await fetchMarketById(marketCode)

  const page = await fetchPage(locale, SLUG)
  const pageLinks = await fetchPageLinks(locale)
  const banners = await fetchBanners(locale, SLUG)

  return {
    props: {
      category: categoriesWithFaqs[0] ?? null,
      market: marketById,
      pageLinks,
      banners,
      ...page,
    },
    notFound: faqs?.length === 0,
    revalidate: 60,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}
